import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { PanouAprobareCerere } from './PanouAprobareCerere'
import { useAuthStore } from '../../../stores/auth'
import { Card } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { eroare } from './comun'
import { CULOARE_STARE_CERERE, ETICHETA_STARE, type Cerere } from './tipuriNecesar'
import { FormularCerere } from './FormularCerere'
import { ModalTransfer } from '../FormularTransfer'
import { motivBonImplicit, esteNecesarTeren } from '@b4l/shared'
import { Modal } from '../../ui/Modal'
import { stareExpeditie } from '../../../lib/stareExpeditie'

/**
 * Necesarul de marfă al unei activități.
 *
 * Firul care se mută aici din mail: cineva de pe teren cere mostre, cine răspunde de gestiune
 * taie ce nu se poate da, iar transferul pleacă separat, când marfa iese din depozit.
 *
 * Cele două coloane, „cerut" și „aprobat", stau una lângă alta deliberat: diferența dintre ele e
 * informația care se pierdea pe mail.
 */

export function FilaNecesar({
  campanieId,
  cod,
}: {
  campanieId: number
  /** Codul activitatii: pe „Necesar teren” formularul aplica regulile cererii „pentru mine”. */
  cod?: string
}) {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const utilizatorId = useAuthStore((s) => s.user?.id)
  /*
    Adminul poate umbla la ciorna altcuiva, EXACT CA PE SERVER: `verificaAutorul` sare peste
    verificare la admin, dar ecranul ascundea butoanele oricui nu era autorul — inclusiv lui.

    S-a vazut pe 01.10.2026, la sponsorizarea „Caravana cu Medici": ciorna era a lui Dorian, iar
    Alexandru, admin, n-avea cum s-o deschida din interfata, desi API-ul i-ar fi raspuns. O
    permisiune care exista pe server si nu se vede pe ecran nu exista.
  */
  const esteAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const poateScrie =
    permissions.includes('marketing.events:write') ||
    permissions.includes('marketing.sampling:write')
  /*
    Aprobarea, expedierea si inchiderea cer `marketing.necesar:aproba` pe server — doar adminul.
    Ecranul le lega de `marketing.sampling:write`, pe care il au cinci roluri: oamenii de pe teren
    vedeau „Verifica si aproba" si „Expediaza", apasau, si primeau refuz.
  */
  const poateAproba = permissions.includes('marketing.necesar:aproba')

  /*
    `?cerere=12` vine din emailul catre aprobatori si din notificari: cererea anume, nu doar fila
    (Madalin, 08.10.2026). Se citeste o singura data — dupa aprobare, panoul nu se mai redeschide.
  */
  const [parametriUrl] = useSearchParams()
  const [dinLink] = useState(() => Number(parametriUrl.get('cerere')) || null)

  const [formularDeschis, setFormularDeschis] = useState(false)
  const [deEditat, setDeEditat] = useState<Cerere | null>(null)
  const [transferDin, setTransferDin] = useState<Cerere | null>(null)
  /* Cererea catre colaborator pentru care se emite AWB-ul si bonul. Vezi `ModalExpediere`. */
  const [expediereDin, setExpediereDin] = useState<Cerere | null>(null)

  const q = useQuery<{ cereri: Cerere[] }>({
    queryKey: ['necesar', campanieId],
    queryFn: async () => (await api.get(`/marketing/necesar/${campanieId}`)).data,
  })
  const cereri = q.data?.cereri ?? []
  const cerereDinLink = dinLink ? cereri.find((c) => c.id === dinLink) : undefined

  const reincarca = () => {
    void qc.invalidateQueries({ queryKey: ['necesar', campanieId] })
    void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
  }

  const actiune = useMutation({
    mutationFn: async (v: { id: number; ce: string; corp?: unknown }) =>
      (await api.post(`/marketing/necesar/cerere/${v.id}/${v.ce}`, v.corp ?? {})).data,
    onSuccess: (_d, v) => {
      toast.success(
        {
          trimite: 'Cerere trimisă',
          retrage: 'Cerere retrasă',
          inchide: 'Cerere închisă',
          'in-activitate': 'Cererea e acum „Pentru activitate” — o poți transfera',
        }[v.ce] ?? 'Salvat',
      )
      reincarca()
    },
    onError: eroare,
  })

  const sterge = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/necesar/cerere/${id}`)).data,
    onSuccess: () => {
      toast.success('Cerere ștearsă')
      reincarca()
    },
    onError: eroare,
  })

  return (
    <div className="space-y-3">
      <Card
        title="Necesar de marfă"
        actions={
          poateScrie ? (
            <Button onClick={() => setFormularDeschis(true)}>Cerere nouă</Button>
          ) : undefined
        }
      >
        {/*
          Linkul din email poate ajunge dupa ce altcineva a rezolvat deja cererea. Se spune, ca sa
          nu caute omul butonul de aprobare care nu mai are de ce sa apara.
        */}
        {cerereDinLink && cerereDinLink.status !== 'trimisa' && (
          <p className="mb-3 rounded border border-b4l-border bg-b4l-bg-soft px-3 py-2 text-sm text-b4l-text">
            Cererea din email nu mai așteaptă aprobare — e{' '}
            <strong>
              {(ETICHETA_STARE[cerereDinLink.status] ?? cerereDinLink.status).toLowerCase()}
            </strong>
            {cerereDinLink.aprobatDe
              ? ` de ${cerereDinLink.aprobatDe.name ?? cerereDinLink.aprobatDe.email}`
              : ''}
            .
          </p>
        )}
        {cereri.length === 0 ? (
          <p className="text-sm text-b4l-text-muted">
            Nicio cerere. Aici se scrie ce mostre trebuie pentru activitate — SKU și cantități — iar
            cine răspunde de gestiune le aprobă și face transferul.
          </p>
        ) : (
          <div className="space-y-4">
            {cereri.map((c) => (
              <RandCerere
                key={c.id}
                cerere={c}
                dinLink={c.id === dinLink}
                eAlMeu={c.ceruteDeId === utilizatorId || esteAdmin}
                poateScrie={poateScrie}
                poateAproba={poateAproba}
                peActiune={(ce, corp) => actiune.mutate({ id: c.id, ce, corp })}
                peEditare={() => setDeEditat(c)}
                peStergere={() => {
                  if (confirm('Ștergi cererea?')) sterge.mutate(c.id)
                }}
                peTransfer={() => setTransferDin(c)}
                peExpediere={() => setExpediereDin(c)}
              />
            ))}
          </div>
        )}
      </Card>

      {(formularDeschis || deEditat) && (
        <FormularCerere
          campanieId={campanieId}
          initial={deEditat}
          teren={esteNecesarTeren(cod)}
          onClose={() => {
            setFormularDeschis(false)
            setDeEditat(null)
          }}
          onGata={reincarca}
        />
      )}

      {transferDin && (
        <ModalTransfer
          campanieId={campanieId}
          cerere={{
            id: transferDin.id,
            /* Cine a cerut devine destinația implicită a transferului. Vezi `FormularTransfer`. */
            ceruteDeId: transferDin.ceruteDeId,
            linii: transferDin.linii.map((l) => ({
              id: l.id,
              productId: l.productId,
              denumire: l.denumire,
              rest: l.rest,
            })),
          }}
          onClose={() => {
            setTransferDin(null)
            reincarca()
          }}
        />
      )}

      {expediereDin && (
        <ModalExpediere
          campanieId={campanieId}
          cerere={expediereDin}
          onClose={() => setExpediereDin(null)}
          onGata={reincarca}
        />
      )}
    </div>
  )
}

/** Un rând de cerere, cu liniile ei și cu butoanele care au sens în starea curentă. */
function RandCerere({
  cerere,
  dinLink,
  eAlMeu,
  poateScrie,
  poateAproba,
  peActiune,
  peEditare,
  peStergere,
  peTransfer,
  peExpediere,
}: {
  cerere: Cerere
  /** Cererea la care trimite linkul din email: evidentiata, si cu aprobarea deja deschisa. */
  dinLink: boolean
  eAlMeu: boolean
  poateScrie: boolean
  poateAproba: boolean
  peActiune: (ce: string, corp?: unknown) => void
  peEditare: () => void
  peStergere: () => void
  peTransfer: () => void
  /** Expediază către colaborator: AWB la FAN, apoi bon de consum. */
  peExpediere: () => void
}) {
  /*
    Venit din email, pe o cerere care asteapta, de la cineva care o poate aproba: panoul e deja
    deschis, cu cantitatile cerute puse la „Aprobat". Ramane un singur clic — Confirma sau Respinge.
  */
  /*
    Aprobarea se face in panoul cu activitatea alaturi, stocul si valoarea (PR & Evenimente,
    etapa 3, 15.10.2026) — acelasi ca in „Cereri de marfa". Venit din email, pe o cerere care
    asteapta, de la cine o poate aproba: panoul e deja deschis.
  */
  const [panouDeschis, setPanouDeschis] = useState(
    () => dinLink && poateAproba && cerere.status === 'trimisa',
  )
  const ceruteDe = cerere.ceruteDe?.name ?? cerere.ceruteDe?.email ?? '—'

  const rand = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // Venit din email: macar se ajunge la cerere, chiar daca e deja rezolvata.
    if (dinLink) rand.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [dinLink])

  return (
    <div
      ref={rand}
      className={`rounded border ${dinLink ? 'border-b4l-blue ring-2 ring-b4l-blue/30' : 'border-b4l-border'}`}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-b4l-border bg-b4l-bg-soft px-3 py-2">
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${CULOARE_STARE_CERERE[cerere.status] ?? ''}`}
        >
          {ETICHETA_STARE[cerere.status] ?? cerere.status}
        </span>
        {/*
          Acțiunea, înaintea descrierii: la un program trimestrial, „pentru care acțiune” e prima
          întrebare, iar textul liber de sub el nu răspundea (Alexandru, 29.09.2026).
        */}
        {/*
          La cererile care pleaca DIRECT, in locul actiunii sta destinatarul: ele n-au actiune, iar
          „fara actiune" spunea ca lipseste ceva, cand de fapt nu era cazul. Cui ii pleaca marfa e
          oricum prima intrebare acolo.
        */}
        {cerere.tip === 'partener' || cerere.tip === 'colaborator' ? (
          /*
            TIPUL SCRIS IN CUVINTE, nu doar o sageata. Pe 12.10.2026 o cerere facuta „Pentru un
            partener" in loc de „Pentru activitate" a ajuns aprobata fara ca cineva sa observe:
            pe rand scria doar „→ Andreea Sirbu", iar Alexandru a cautat butonul de transfer,
            care la cererile directe nu exista.
          */
          <span
            className="rounded bg-b4l-purple-light px-2 py-0.5 text-xs font-medium text-b4l-purple-dark"
            title="Pleacă direct prin curier, cu AWB și bon de consum. Nu trece prin gestiunea unui coleg, deci nu se transferă."
          >
            {cerere.tip === 'partener'
              ? `Către partener: ${cerere.client?.name ?? destinatarul(cerere).nume}${
                  cerere.livrare?.nume && cerere.livrare.nume !== cerere.client?.name
                    ? ` · livrare la ${cerere.livrare.nume}`
                    : ''
                }`
              : `Către colaborator: ${destinatarul(cerere).nume}`}
          </span>
        ) : cerere.actiune ? (
          <span className="rounded bg-b4l-blue-light px-2 py-0.5 text-xs font-medium text-b4l-blue-dark">
            {cerere.actiune.denumire}
          </span>
        ) : (
          <span
            className="text-xs text-b4l-text-dim"
            title="Cerere pe activitate, fără o acțiune anume"
          >
            fără acțiune
          </span>
        )}
        {cerere.tip === 'activitate' && cerere.prinCurier && (
          <span
            className="rounded bg-b4l-purple-light px-2 py-0.5 text-xs font-medium text-b4l-purple-dark"
            title={`Prin curier, AWB după aprobare: ${destinatarul(cerere).adresa}`}
          >
            Prin curier: {destinatarul(cerere).nume}
            {cerere.livrare?.localitate ? ` · ${cerere.livrare.localitate}` : ''}
          </span>
        )}
        <span className="text-sm text-b4l-text">{cerere.motiv || 'Fără descriere'}</span>
        <span className="text-xs text-b4l-text-muted">· cerut de {ceruteDe}</span>
        {cerere.aprobatDe && (
          <span className="text-xs text-b4l-text-muted">
            ·{' '}
            {cerere.status === 'respinsa'
              ? 'respins'
              : cerere.status === 'returnata'
                ? 'trimis înapoi'
                : 'rezolvat'}{' '}
            de {cerere.aprobatDe.name ?? cerere.aprobatDe.email}
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-1">
          <Butoane
            cerere={cerere}
            eAlMeu={eAlMeu}
            poateScrie={poateScrie}
            poateAproba={poateAproba}
            inAprobare={panouDeschis}
            peActiune={peActiune}
            peEditare={peEditare}
            peStergere={peStergere}
            peTransfer={peTransfer}
            peExpediere={peExpediere}
            peAprobare={() => setPanouDeschis(true)}
          />
        </div>
      </div>

      {cerere.motivRespingere &&
        (cerere.status === 'returnata' ? (
          <p className="border-b border-b4l-border bg-b4l-amber-light px-3 py-2 text-sm text-b4l-amber-dark">
            <strong>De corectat:</strong> {cerere.motivRespingere}
            {eAlMeu && poateScrie && (
              <span className="block text-xs">
                Editează cererea după notă, apoi „Trimite din nou”.
              </span>
            )}
          </p>
        ) : (
          <p className="border-b border-b4l-border px-3 py-2 text-sm text-b4l-red-dark">
            {cerere.motivRespingere}
          </p>
        ))}

      <Linii cerere={cerere} />

      {panouDeschis && (
        <PanouAprobareCerere
          cerere={cerere}
          onInchide={() => setPanouDeschis(false)}
          onDecis={() => setPanouDeschis(false)}
        />
      )}
    </div>
  )
}

/**
 * Cerere „către partener / colaborator" → „Pentru activitate", cât marfa n-a plecat încă.
 *
 * Pe 12.10.2026 o cerere făcută „Pentru un partener" în loc de „Pentru activitate" n-avea altă
 * ieșire decât închiderea și o cerere nouă. Serverul refuză după AWB, bon sau proces-verbal —
 * butonul nici nu apare atunci. Vezi `schimbaInActivitate`.
 */
function ButonInActivitate({
  cerere,
  peActiune,
}: {
  cerere: Cerere
  peActiune: (ce: string) => void
}) {
  if (cerere.tip !== 'partener' && cerere.tip !== 'colaborator') return null
  if (cerere.awbNumber || cerere.bonNrDoc) return null
  return (
    <Button
      variant="secondary"
      className="!px-2 !py-1 !text-xs"
      title="Pentru când marfa o duce un coleg, nu pleacă prin curier"
      onClick={() => {
        if (
          confirm(
            'Schimbi cererea în „Pentru activitate”?\n\n' +
              'Destinatarul extern și adresa de livrare se șterg, cantitățile aprobate rămân. ' +
              'Marfa nu mai pleacă prin curier: se transferă în gestiunea colegului care o duce, ' +
              'cu „Transferă restul”.',
          )
        )
          peActiune('in-activitate')
      }}
    >
      Schimbă în „Pentru activitate”
    </Button>
  )
}

/** Butoanele care au sens în starea curentă. Separate, ca rândul să rămână citibil. */
function Butoane({
  cerere,
  eAlMeu,
  poateScrie,
  poateAproba,
  inAprobare,
  peActiune,
  peEditare,
  peStergere,
  peTransfer,
  peExpediere,
  peAprobare,
}: {
  cerere: Cerere
  eAlMeu: boolean
  poateScrie: boolean
  poateAproba: boolean
  inAprobare: boolean
  peActiune: (ce: string) => void
  peEditare: () => void
  peStergere: () => void
  peTransfer: () => void
  /** Expediază către colaborator: AWB la FAN, apoi bon de consum. */
  peExpediere: () => void
  peAprobare: () => void
}) {
  /* Transferul cere `marketing.sampling:write` pe server — dreptul celor care muta marfa. */
  const poateTransfera = useAuthStore((st) => st.permissions).includes('marketing.sampling:write')
  const mic = '!px-2 !py-1 !text-xs'
  if ((cerere.status === 'ciorna' || cerere.status === 'returnata') && poateScrie && eAlMeu) {
    return (
      <>
        <Button variant="secondary" className={mic} onClick={peEditare}>
          Editează
        </Button>
        <Button className={mic} onClick={() => peActiune('trimite')}>
          {cerere.status === 'returnata' ? 'Trimite din nou' : 'Trimite'}
        </Button>
        <Button variant="danger" className={mic} onClick={peStergere}>
          Șterge
        </Button>
      </>
    )
  }
  if (cerere.status === 'trimisa') {
    return (
      <>
        {poateScrie && eAlMeu && (
          <Button variant="secondary" className={mic} onClick={() => peActiune('retrage')}>
            Retrage
          </Button>
        )}
        {poateAproba && !inAprobare && (
          <Button className={mic} onClick={peAprobare}>
            Verifică și aprobă
          </Button>
        )}
        {poateAproba && !inAprobare && <ButonInActivitate cerere={cerere} peActiune={peActiune} />}
      </>
    )
  }
  if (cerere.status === 'aprobata' || cerere.status === 'transferata') {
    return (
      <>
        {/*
          CATRE COLABORATOR SAU PARTENER nu se transfera nimic: marfa pleaca direct, cu AWB si bon
          de consum. „Transfera restul" n-ar avea ce muta si unde.

          Ce s-a expediat se vede de catre TOTI cei care vad cererea — cine a cerut marfa vrea sa
          stie unde e coletul. Butoanele raman ale celor care au dreptul, exact ca pe server.
        */}
        {cerere.tip === 'colaborator' || cerere.tip === 'partener' ? (
          cerere.awbNumber ? (
            <>
              {/*
                AWB-ul, cu starea coletului alaturi. Urmarirea exista deja la Bani →
                Rambursuri curieri, alimentata din borderoul zilnic al lui FAN — un AWB emis de
                aici intra acolo singur. Lipsea doar vederea: pe cerere se vedea numarul, dar
                trebuia sa treci in alt ecran ca sa afli unde a ajuns coletul.

                Numarul duce la ecranul de expeditii, cautat dupa el.
              */}
              <a
                href={`/compliance/rambursuri-curieri?tab=expeditii&q=${cerere.awbNumber}`}
                className="rounded-full bg-b4l-green-light px-2 py-1 text-xs text-b4l-green-dark hover:underline"
                title={`Vezi expediția în Rambursuri curieri${cerere.awbDe ? ` · emis de ${cerere.awbDe.name}` : ''}`}
              >
                AWB {cerere.awbNumber}
                {cerere.awbCost != null ? ` · ${cerere.awbCost} lei` : ''}
              </a>
              <StareColet stare={cerere.stareAwb} />
              {cerere.awbDe && (
                <span className="text-xs text-b4l-text-muted">· emis de {cerere.awbDe.name}</span>
              )}
              {cerere.bonEaDocNo && (
                <span className="rounded-full bg-b4l-blue-light px-2 py-1 text-xs text-b4l-blue-dark">
                  Bon {cerere.bonEaDocNo}
                </span>
              )}
              {/*
                PROCESUL-VERBAL, doar la partener: se printeaza, se pune in colet si se intoarce
                semnat. Se deschide intr-o fila noua, nu se descarca — de cele mai multe ori se
                trimite direct la imprimanta.

                Apare dupa bon fiindca de acolo isi ia cantitatile si preturile: bonul le-a
                inghetat la emitere, iar un PV cu alte valori decat documentul contabil ar fi
                exact nepotrivirea pe care o cauta un control.
              */}
              {cerere.tip === 'partener' && cerere.bonNrDoc && (
                <a
                  href={`/api/marketing/necesar/cerere/${cerere.id}/pv`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded border border-b4l-border px-2 py-1 text-xs text-b4l-text hover:bg-b4l-bg"
                  title="Proces-verbal de predare-primire, pentru semnătura beneficiarului"
                >
                  {cerere.pvNr ? `PV ${cerere.pvNr}` : 'Proces-verbal'}
                </a>
              )}
              {/*
                BONUL CAZUT DUPA AWB: coletul a plecat, documentul contabil nu. Fara butonul asta
                cererea ramanea blocata — „Expediaza" dispare odata ce exista AWB.
              */}
              {poateAproba && <ButonAnuleazaAwb cerere={cerere} />}
              {!cerere.bonNrDoc && poateAproba && (
                <Button className={mic} onClick={peExpediere}>
                  Emite bonul
                </Button>
              )}
            </>
          ) : cerere.awbInCursLa ? (
            <AwbInCurs cerere={cerere} poateAproba={poateAproba} />
          ) : (
            poateAproba && (
              <>
                <Button className={mic} onClick={peExpediere}>
                  Expediază
                </Button>
                <ButonInActivitate cerere={cerere} peActiune={peActiune} />
              </>
            )
          )
        ) : (
          <>
            {/*
              PENTRU ACTIVITATE, PRIN CURIER (16.10.2026): marfa merge tot prin transfer; AWB-ul e
              doar transportul, cu datele de pe cerere. Fara bon aici.
            */}
            {cerere.prinCurier &&
              (cerere.awbNumber ? (
                <>
                  <a
                    href={`/compliance/rambursuri-curieri?tab=expeditii&q=${cerere.awbNumber}`}
                    className="rounded-full bg-b4l-green-light px-2 py-1 text-xs text-b4l-green-dark hover:underline"
                    title="Vezi expediția în Rambursuri curieri"
                  >
                    AWB {cerere.awbNumber}
                    {cerere.awbCost != null ? ` · ${cerere.awbCost} lei` : ''}
                  </a>
                  <StareColet stare={cerere.stareAwb} />
                  {cerere.awbDe && (
                    <span className="text-xs text-b4l-text-muted">
                      · emis de {cerere.awbDe.name}
                    </span>
                  )}
                  {poateAproba && <ButonAnuleazaAwb cerere={cerere} />}
                </>
              ) : cerere.awbInCursLa ? (
                <AwbInCurs cerere={cerere} poateAproba={poateAproba} />
              ) : (
                poateAproba && (
                  <Button className={mic} onClick={peExpediere}>
                    Generează AWB
                  </Button>
                )
              ))}
            {poateTransfera && cerere.restTotal > 0 && (
              <Button className={mic} onClick={peTransfer}>
                Transferă restul
              </Button>
            )}
          </>
        )}
        {poateAproba && (
          <Button
            variant="secondary"
            className={mic}
            onClick={() => {
              if (confirm('Închizi cererea? Restul nu se mai dă.')) peActiune('inchide')
            }}
          >
            Închide
          </Button>
        )}
      </>
    )
  }
  if (cerere.status === 'respinsa' && poateScrie && eAlMeu) {
    return (
      <Button variant="danger" className={mic} onClick={peStergere}>
        Șterge
      </Button>
    )
  }
  return null
}

/**
 * AWB cerut la FAN, fără rezultat pe cerere. Fie apelul încă rulează, fie FAN n-a răspuns — și
 * atunci AWB-ul poate să fi ieșit. „Expediază” nu mai apare: a doua apăsare ar scoate al doilea
 * AWB plătit. Un administrator verifică în SelfAWB și spune ce a găsit.
 */
function AwbInCurs({ cerere, poateAproba }: { cerere: Cerere; poateAproba: boolean }) {
  const [deschis, setDeschis] = useState(false)
  const pastila = 'rounded-full bg-b4l-amber-light px-2 py-1 text-xs text-b4l-amber-dark'
  if (!cerere.awbNesigur) {
    return (
      <span className={pastila}>
        Se emite AWB-ul{cerere.awbDe ? ` (${cerere.awbDe.name})` : ''}…
      </span>
    )
  }
  return (
    <>
      <span className={pastila} title={cerere.awbEroare ?? undefined}>
        AWB fără răspuns de la FAN
      </span>
      {poateAproba && (
        <Button className="!px-2 !py-1 !text-xs" onClick={() => setDeschis(true)}>
          Am verificat în SelfAWB
        </Button>
      )}
      {deschis && <ModalAwbFaraRaspuns cerere={cerere} onClose={() => setDeschis(false)} />}
    </>
  )
}

function ModalAwbFaraRaspuns({ cerere, onClose }: { cerere: Cerere; onClose: () => void }) {
  const qc = useQueryClient()
  const [awb, setAwb] = useState('')
  const d = destinatarul(cerere)
  const cand = cerere.awbInCursLa
    ? new Date(cerere.awbInCursLa).toLocaleString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''
  const salveaza = useMutation({
    mutationFn: async (gasit: string | null) =>
      (
        await api.post<{ awbNumber: string | null }>(
          `/marketing/necesar/cerere/${cerere.id}/awb-fara-raspuns`,
          { awb: gasit },
        )
      ).data,
    onSuccess: (r) => {
      toast.success(
        r.awbNumber
          ? `AWB ${r.awbNumber} salvat pe cerere. Emite bonul de consum.`
          : 'Gata — poți apăsa din nou „Expediază”.',
      )
      void qc.invalidateQueries({ queryKey: ['necesar'] })
      void qc.invalidateQueries({ queryKey: ['campanie', cerere.campanieId] })
      onClose()
    },
    onError: eroare,
  })
  return (
    <Modal open onClose={onClose} title="AWB fără răspuns de la FAN">
      <div className="space-y-3 p-4 text-sm">
        <p className="text-b4l-text">
          {cerere.awbDe ? `${cerere.awbDe.name} a cerut` : 'S-a cerut'} un AWB la FAN pe {cand}, dar
          răspunsul nu a venit. Este posibil ca AWB-ul să fi fost emis totuși — și se plătește
          oricum.
        </p>
        <p className="text-b4l-text-muted">
          Caută în SelfAWB (fancourier.ro) un AWB din {cand.split(',')[0]} către{' '}
          <b className="text-b4l-text">{d.nume}</b>.
        </p>
        <label className="block text-xs text-b4l-text-muted">
          Numărul AWB-ului găsit
          <input
            value={awb}
            onChange={(e) => setAwb(e.target.value)}
            inputMode="numeric"
            placeholder="ex. 7000172004511"
            className="mt-1 w-full rounded border border-b4l-border bg-b4l-surface px-2 py-1.5 text-sm text-b4l-text"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && awb.trim()) salveaza.mutate(awb.trim())
            }}
          />
        </label>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button
            variant="secondary"
            disabled={salveaza.isPending}
            onClick={() => salveaza.mutate(null)}
          >
            Nu există — expediază din nou
          </Button>
          <Button
            disabled={!awb.trim() || salveaza.isPending}
            loading={salveaza.isPending}
            onClick={() => salveaza.mutate(awb.trim())}
          >
            Salvează AWB-ul găsit
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * ANULEAZĂ AWB-UL (16.10.2026), cât coletul n-a fost ridicat de curier.
 *
 * Nu apare când urmărirea FAN are deja o mișcare: acolo FAN refuză oricum, iar butonul ar promite
 * ce nu se poate. Bonul de consum nu se atinge — o spune fereastra, ca nimeni să nu creadă că marfa
 * s-a întors în gestiune.
 */
function ButonAnuleazaAwb({ cerere }: { cerere: Cerere }) {
  const [deschis, setDeschis] = useState(false)
  const laCurier = !!(cerere.stareAwb?.ultimaMiscare || cerere.stareAwb?.livratLa)
  if (laCurier) return null
  return (
    <>
      <Button variant="secondary" className="!px-2 !py-1 !text-xs" onClick={() => setDeschis(true)}>
        Anulează AWB-ul
      </Button>
      {deschis && <ModalAnuleazaAwb cerere={cerere} onClose={() => setDeschis(false)} />}
    </>
  )
}

function ModalAnuleazaAwb({ cerere, onClose }: { cerere: Cerere; onClose: () => void }) {
  const qc = useQueryClient()
  const [motiv, setMotiv] = useState('')
  const anuleaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ awbAnulat: string }>(
          `/marketing/necesar/cerere/${cerere.id}/anuleaza-awb`,
          { motiv: motiv.trim() },
          { timeout: 60_000 },
        )
      ).data,
    onSuccess: (r) => {
      toast.success(`AWB ${r.awbAnulat} anulat. Depozitul a fost anunțat.`)
      void qc.invalidateQueries({ queryKey: ['necesar'] })
      void qc.invalidateQueries({ queryKey: ['campanie', cerere.campanieId] })
      onClose()
    },
    onError: eroare,
  })
  return (
    <Modal open onClose={onClose} title={`Anulezi AWB-ul ${cerere.awbNumber}?`}>
      <div className="space-y-3 p-4 text-sm">
        <p className="text-b4l-text">
          AWB-ul se șterge la FAN Courier și de pe cerere. Depozitul primește email să nu mai
          pregătească coletul. Merge doar cât coletul n-a fost ridicat de curier.
        </p>
        {cerere.bonNrDoc && (
          <p className="rounded border border-b4l-amber/40 bg-b4l-amber-light px-2 py-1.5 text-xs text-b4l-amber-dark">
            Bonul de consum {cerere.bonEaDocNo ?? cerere.bonNrDoc} <strong>rămâne</strong>: marfa a
            ieșit din gestiune, iar bonul nu se poate anula din CRM. Un AWB nou pe cererea asta iese
            fără bon nou.
          </p>
        )}
        <label className="block text-xs text-b4l-text-muted">
          De ce se anulează
          <input
            value={motiv}
            onChange={(e) => setMotiv(e.target.value)}
            placeholder="ex. adresa greșită, evenimentul s-a amânat"
            className="mt-1 w-full rounded border border-b4l-border bg-b4l-surface px-2 py-1.5 text-sm text-b4l-text"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && motiv.trim().length >= 3) anuleaza.mutate()
            }}
          />
        </label>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button
            variant="danger"
            disabled={motiv.trim().length < 3}
            loading={anuleaza.isPending}
            onClick={() => anuleaza.mutate()}
          >
            Anulează AWB-ul
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/** Liniile cererii: cerut, aprobat (editabil în timpul aprobării), transferat, rest. */
function Linii({ cerere }: { cerere: Cerere }) {
  const camp = (l: Cerere['linii'][number]) => (
    <span className="tabular-nums">{l.cantitateAprobata ?? '—'}</span>
  )

  return (
    <>
      {/*
        Pe telefon, card pe rand. Tabelul are cinci coloane si niciun scroll orizontal: pe 360 px
        denumirile cad pe sase randuri si coloana „Rest" iese din ecran, iar casuta de cantitate
        aprobata devine inaccesibila — adica exact campul pentru care se deschide aprobarea
        (Victor, 23.09.2026). Acelasi tipar ca la fila Teren.
      */}
      <ul className="space-y-2 p-3 md:hidden">
        {cerere.linii.map((l) => (
          <li key={l.id} className="rounded-lg border border-b4l-border p-3">
            <p className="text-sm font-medium text-b4l-text">{l.denumire}</p>
            <p className="mt-0.5 text-xs text-b4l-text-dim">{l.sku}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-b4l-text-muted">
              <span>
                Cerut <span className="tabular-nums text-b4l-text">{l.cantitateCeruta}</span>
              </span>
              <span className={l.peste ? 'text-b4l-red-dark' : ''}>
                Transferat <span className="tabular-nums">{l.transferat}</span>
                {l.peste ? ' ⚠' : ''}
              </span>
              <span>
                Rest <span className="tabular-nums">{l.rest}</span>
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-b4l-text-muted">Aprobat</span>
              {camp(l)}
            </div>
          </li>
        ))}
      </ul>

      <table className="hidden w-full text-sm md:table">
        <thead className="text-left text-xs uppercase tracking-wide text-b4l-text-dim">
          <tr>
            <th className="px-3 py-1.5">Produs</th>
            <th className="w-24 text-right">Cerut</th>
            <th className="w-32 text-right">Aprobat</th>
            <th className="w-28 text-right">Transferat</th>
            <th className="w-24 px-3 text-right">Rest</th>
          </tr>
        </thead>
        <tbody>
          {cerere.linii.map((l) => (
            <tr key={l.id} className="border-t border-b4l-border/60">
              <td className="px-3 py-1.5">
                {l.denumire} <span className="text-xs text-b4l-text-dim">{l.sku}</span>
              </td>
              <td className="text-right tabular-nums">{l.cantitateCeruta}</td>
              <td className="text-right tabular-nums">{camp(l)}</td>
              <td className={`text-right tabular-nums ${l.peste ? 'text-b4l-red-dark' : ''}`}>
                {l.transferat}
                {l.peste ? ' ⚠' : ''}
              </td>
              <td className="px-3 text-right tabular-nums">{l.rest}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cerere.linii.some((l) => l.peste) && (
        <p className="px-3 py-2 text-xs text-b4l-red-dark">
          Pe o linie s-a transferat mai mult decât s-a aprobat. Se întâmplă când doi colegi trimit
          în același timp — marfa a ieșit oricum din depozit, deci verifică documentele în
          ExpertAccounts.
        </p>
      )}
    </>
  )
}

/**
 * Expedierea catre un colaborator: AWB la FAN Courier, apoi bonul de consum.
 *
 * ORDINEA E SCRISA IN SERVICIU si nu se poate inversa de aici — AWB-ul se poate sterge prin API,
 * bonul de consum nu. Modalul doar strange ce lipseste: din ce gestiune iese marfa si cat
 * cantareste coletul, cand produsele n-au greutatea in catalog.
 *
 * Greutatea vine gata calculata de pe server, din greutatea produselor × cantitatea aprobata. Cand
 * macar un produs n-o are, campul ramane gol si se cere: o suma partiala ar fi mai rea decat
 * nimic, fiindca FAN recantareste coletul si factureaza diferenta.
 */
function ModalExpediere({
  campanieId,
  cerere,
  onClose,
  onGata,
}: {
  campanieId: number
  cerere: Cerere
  onClose: () => void
  onGata: () => void
}) {
  /*
    DOAR BONUL: cererea are AWB, dar bonul a cazut dupa ce coletul plecase. Aceeasi fereastra,
    fara greutate — coletul e deja la curier —, si alt capat pe server (`emite-bon`).
  */
  const doarBon = !!cerere.awbNumber
  /*
    DOAR AWB-UL: cerere „Pentru activitate" bifata „Trimite prin curier" (16.10.2026). Marfa merge
    prin transfer, ca pana acum; de aici pleaca doar transportul — fara gestiune si fara bon.
  */
  /* Bonul a ieșit deja, cu un AWB anulat între timp: AWB-ul nou e doar transportul. */
  const bonDejaEmis = !doarBon && !!cerere.bonNrDoc
  const doarAwb = cerere.tip === 'activitate' || bonDejaEmis
  const [locId, setLocId] = useState('')
  const [greutate, setGreutate] = useState('')
  const [motiv, setMotiv] = useState('')
  /*
    GESTIUNE PE PRODUS. EA primeste gestiunea pe fiecare rand al bonului (Alexandru, 12.10.2026:
    „in Expert se poate face un bon de consum din mai multe gestiuni"). Pana atunci un colet cu
    mostre din „Mostre" si pungi din „Materiale mk" cerea doua transferuri doar ca sa ajunga totul
    in Sampling. Gol pe un produs = gestiunea de sus.
  */
  const [gestiuniDiferite, setGestiuniDiferite] = useState(false)
  const [peProdus, setPeProdus] = useState<Record<number, string>>({})
  const liniiAprobate = cerere.linii.filter((l) => (l.cantitateAprobata ?? l.cantitateCeruta) > 0)
  const gestiuneaProdusului = (productId: number) =>
    (gestiuniDiferite && peProdus[productId]) || locId
  const gestiuniFolosite = [
    ...new Set(liniiAprobate.map((l) => gestiuneaProdusului(l.productId)).filter(Boolean)),
  ]
  /* Stocul fiecarui produs in gestiunea aleasa pentru el — acelasi catalog ca la transfer. */
  const stocuri = useQueries({
    queries: gestiuniDiferite
      ? gestiuniFolosite.map((g) => ({
          queryKey: ['sampling', 'produse', g],
          queryFn: async () =>
            (
              await api.get<{ produse: { id: number; stocEa: number | null }[] }>(
                '/marketing/sampling/produse',
                { params: { locId: Number(g) } },
              )
            ).data.produse,
        }))
      : [],
  })
  const stocIn = (g: string, productId: number): number | null => {
    const i = gestiuniFolosite.indexOf(g)
    return stocuri[i]?.data?.find((p) => p.id === productId)?.stocEa ?? null
  }

  const gestiune = useQuery({
    enabled: !doarAwb,
    queryKey: ['gestiune-consum', campanieId],
    queryFn: async () =>
      (
        await api.get<{ propusa: number | null; gestiuni: { id: number; nume: string }[] }>(
          `/marketing/costuri/${campanieId}/gestiune-consum`,
        )
      ).data,
  })
  useEffect(() => {
    if (!locId && gestiune.data?.propusa) setLocId(String(gestiune.data.propusa))
  }, [gestiune.data, locId])

  const calculata = useQuery<Greutate>({
    enabled: !doarBon,
    queryKey: ['greutate-cerere', cerere.id],
    queryFn: async () =>
      (await api.get<Greutate>(`/marketing/necesar/cerere/${cerere.id}/greutate`)).data,
  })
  useEffect(() => {
    if (!greutate && calculata.data?.total) setGreutate(String(calculata.data.total))
  }, [calculata.data, greutate])

  const expediaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          awb: string
          awbCost: number | null
          bonEaDocNo: string | null
          bonMotiv: string | null
        }>(
          `/marketing/necesar/cerere/${cerere.id}/${doarBon ? 'emite-bon' : 'expediaza'}`,
          {
            locId: doarAwb ? undefined : Number(locId),
            gestiuniPeLinie:
              !doarAwb && gestiuniDiferite
                ? liniiAprobate
                    .filter((l) => peProdus[l.productId] && peProdus[l.productId] !== locId)
                    .map((l) => ({ productId: l.productId, locId: Number(peProdus[l.productId]) }))
                : undefined,
            ...(doarBon ? {} : { greutateGrame: greutate ? Number(greutate) : undefined }),
            motiv: doarAwb ? undefined : motiv.trim() || undefined,
          },
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: (r) => {
      /* Bonul poate cadea dupa ce AWB-ul a plecat — coletul chiar pleaca, deci nu e o eroare. */
      if (r.bonMotiv) {
        toast.warning(
          doarBon
            ? `Bonul n-a plecat: ${r.bonMotiv}`
            : `AWB ${r.awb} emis, dar bonul n-a plecat: ${r.bonMotiv}`,
        )
      } else {
        toast.success(`AWB ${r.awb}${r.bonEaDocNo ? ` · bon ${r.bonEaDocNo}` : ''}`)
      }
      onGata()
      onClose()
    },
    onError: (err) => {
      eroare(err)
      /* Fara raspuns de la FAN, cererea ramane rezervata — randul trebuie sa arate asta. */
      onGata()
    },
  })

  const d = destinatarul(cerere)
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        doarBon
          ? `Bonul de consum pentru AWB ${cerere.awbNumber}`
          : doarAwb
            ? 'AWB prin curier'
            : cerere.tip === 'partener'
              ? 'Expediază către partener'
              : 'Expediază către colaborator'
      }
    >
      <div className="space-y-3 text-sm">
        <div className="rounded border border-b4l-border bg-b4l-bg p-3">
          <p className="font-medium text-b4l-text">{d.nume}</p>
          <p className="text-xs text-b4l-text-muted">
            {d.adresa || d.undeSeCompleteaza}
            {d.telefon ? ` · ${d.telefon}` : ''}
          </p>
          <p className="mt-1 text-xs text-b4l-text-muted">
            {cerere.linii
              .filter((l) => (l.cantitateAprobata ?? 0) > 0)
              .map((l) => `${l.denumire} ×${l.cantitateAprobata}`)
              .join(', ')}
          </p>
        </div>

        {!doarAwb && (
          <>
            <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
              Din ce gestiune iese marfa
              <select
                value={locId}
                onChange={(e) => setLocId(e.target.value)}
                className="rounded border border-b4l-border px-2 py-1.5 text-sm"
              >
                <option value="">— alege —</option>
                {(gestiune.data?.gestiuni ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nume} ({g.id})
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-xs text-b4l-text-muted">
              <input
                type="checkbox"
                checked={gestiuniDiferite}
                onChange={(e) => setGestiuniDiferite(e.target.checked)}
              />
              Produsele ies din gestiuni diferite
            </label>
            {gestiuniDiferite && (
              <div className="space-y-1 rounded border border-b4l-border p-2">
                {liniiAprobate.map((l) => {
                  const g = gestiuneaProdusului(l.productId)
                  const stoc = g ? stocIn(g, l.productId) : null
                  const cantitate = l.cantitateAprobata ?? l.cantitateCeruta
                  return (
                    <div key={l.productId} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 text-b4l-text">
                        {l.denumire} <span className="text-b4l-text-dim">×{cantitate}</span>
                      </span>
                      <select
                        value={peProdus[l.productId] ?? ''}
                        onChange={(e) =>
                          setPeProdus((v) => ({ ...v, [l.productId]: e.target.value }))
                        }
                        className="w-56 rounded border border-b4l-border px-2 py-1 text-xs"
                      >
                        <option value="">ca mai sus</option>
                        {(gestiune.data?.gestiuni ?? []).map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.nume} ({x.id})
                          </option>
                        ))}
                      </select>
                      <span
                        className={`w-20 text-right ${stoc != null && stoc < cantitate ? 'text-b4l-red' : 'text-b4l-text-dim'}`}
                      >
                        {stoc == null ? '' : `stoc ${stoc}`}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {!doarBon && (
          <BlocGreutate
            cerereId={cerere.id}
            date={calculata.data}
            greutate={greutate}
            setGreutate={setGreutate}
          />
        )}

        {!doarAwb && (
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Ce scrie pe bonul de consum
            <input
              value={motiv}
              onChange={(e) => setMotiv(e.target.value)}
              placeholder={motivBonImplicit(cerere.campanie.tip ?? '', cerere.tip ?? '', d.nume)}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            />
          </label>
        )}

        {doarAwb ? (
          <p className="text-xs text-b4l-text-muted">
            {bonDejaEmis ? (
              <>
                Se emite <strong>doar un AWB nou</strong>. Bonul de consum{' '}
                {cerere.bonEaDocNo ?? cerere.bonNrDoc} a ieșit deja, cu AWB-ul anulat, și nu se
                emite a doua oară.
              </>
            ) : (
              <>
                Se emite <strong>doar AWB-ul</strong> la FAN Courier, cu datele de pe cerere. Marfa
                pentru activitate merge prin transfer, ca până acum, iar bonul de consum nu se emite
                de aici. AWB-ul se poate anula cât coletul n-a fost ridicat.
              </>
            )}
          </p>
        ) : doarBon ? (
          <p className="text-xs text-b4l-text-muted">
            Coletul a plecat deja cu <strong>AWB {cerere.awbNumber}</strong>. Se emite doar bonul de
            consum, care scoate marfa din gestiune. AWB-ul nu se atinge.
          </p>
        ) : (
          <p className="text-xs text-b4l-text-muted">
            Se emite întâi <strong>AWB-ul</strong> la FAN Courier, apoi bonul de consum. Ordinea
            contează: AWB-ul se poate șterge, bonul de consum nu.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button
            disabled={doarAwb ? !greutate : !locId || (!doarBon && !greutate)}
            loading={expediaza.isPending}
            onClick={() => expediaza.mutate()}
          >
            {doarBon ? 'Emite bonul' : doarAwb ? 'Emite AWB-ul' : 'Emite AWB și bon'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Cine primește coletul și de unde se ia adresa.
 *
 * La COLABORATOR adresa stă pe fișa lui, în Marketing → Influenceri: omul e același, adresa nu se
 * schimbă. La PARTENER stă pe cerere: la o sponsorizare produsele ajung unde se ține evenimentul,
 * nu la sediul fiscal al beneficiarului.
 *
 * Mesajul când lipsește adresa spune EXACT unde se completează — sunt două locuri diferite, iar
 * un „adresa lipsește" fără drum ar trimite omul să caute.
 */
function destinatarul(c: Cerere): {
  nume: string
  adresa: string
  telefon: string | null
  undeSeCompleteaza: string
} {
  /* „Trimite prin curier": pe AWB pleacă datele de pe cerere, la orice tip. */
  if (c.prinCurier) {
    const l = c.livrare
    return {
      nume:
        [l?.contact || l?.nume, c.tip === 'partener' ? l?.nume : null]
          .filter((x, i, a) => x && a.indexOf(x) === i)
          .join(' · ') || '(fără destinatar)',
      adresa: [
        [l?.strada, l?.numar].filter(Boolean).join(' '),
        [
          l?.bloc && `bl. ${l.bloc}`,
          l?.scara && `sc. ${l.scara}`,
          l?.etaj && `et. ${l.etaj}`,
          l?.apartament && `ap. ${l.apartament}`,
        ]
          .filter(Boolean)
          .join(' '),
        l?.localitate,
        l?.judet,
      ]
        .filter(Boolean)
        .join(', '),
      telefon: [l?.telefon, l?.email].filter(Boolean).join(' · ') || null,
      undeSeCompleteaza:
        'Datele de livrare lipsesc — se completează pe cerere, la „Trimite prin curier"',
    }
  }
  if (c.tip === 'partener') {
    const l = c.livrare
    return {
      nume: l?.nume || c.client?.name || '(fără destinatar)',
      adresa: [l?.strada, l?.numar, l?.localitate, l?.judet].filter(Boolean).join(', '),
      telefon: l?.telefon ?? null,
      undeSeCompleteaza: 'Adresa lipsește — se completează pe cerere, la „Unde pleacă coletul"',
    }
  }
  const i = c.influencer
  return {
    nume: i?.numeDestinatar ?? i?.handle ?? '(fără destinatar)',
    adresa: [i?.strada, i?.numarStrada, i?.localitate, i?.judet].filter(Boolean).join(', '),
    telefon: i?.telefon ?? null,
    undeSeCompleteaza: 'Adresa lipsește — se completează în Marketing → Influenceri',
  }
}

interface LinieGreutate {
  productId: number
  sku: string
  denumire: string
  cantitate: number
  greutateUnitara: number | null
  subtotal: number | null
}

interface Greutate {
  linii: LinieGreutate[]
  calculat: number
  lipsesc: number
  total: number | null
}

/**
 * Greutatea coletului, cu socoteala la vedere.
 *
 * Alexandru, 01.10.2026: „să se calculeze greutatea dacă există greutate unitară, altfel să
 * permită introducere manuală".
 *
 * Până acum era totul sau nimic: un singur produs fără greutate în catalog anula calculul pentru
 * tot coletul, iar omul tasta o cifră ghicită pentru toate cele patru produse deodată. Cu
 * greutățile completate doar la o parte din catalog, asta se întâmpla aproape de fiecare dată.
 *
 * Acum se adună ce se știe și se cere doar ce lipsește — o bucată, nu un colet. Iar ce scrii se
 * salvează și în catalog, ca să nu te mai întrebe a doua oară: așa se completează singur, din
 * lucrul de zi cu zi.
 */
function BlocGreutate({
  cerereId,
  date,
  greutate,
  setGreutate,
}: {
  cerereId: number
  date: Greutate | undefined
  greutate: string
  setGreutate: (v: string) => void
}) {
  const qc = useQueryClient()
  const [lipsa, setLipsa] = useState<Record<number, string>>({})

  const completeaza = useMutation({
    mutationFn: async () => {
      const valori = Object.entries(lipsa)
        .map(([productId, g]) => ({ productId: Number(productId), greutateGrame: Number(g) }))
        .filter((v) => Number.isFinite(v.greutateGrame) && v.greutateGrame > 0)
      return (
        await api.post<{ completate: number; greutate: Greutate }>(
          `/marketing/necesar/cerere/${cerereId}/greutati-produse`,
          { valori },
        )
      ).data
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['greutate-cerere', cerereId] })
      setLipsa({})
      if (r.greutate.total) setGreutate(String(r.greutate.total))
      toast.success(`${r.completate} greutăți completate în catalog.`)
    },
    onError: eroare,
  })

  if (!date) return <p className="text-xs text-b4l-text-muted">Se calculează greutatea…</p>

  const deCompletat = Object.values(lipsa).filter((v) => Number(v) > 0).length

  return (
    <div className="space-y-2 rounded border border-b4l-border bg-b4l-bg p-3">
      <div className="text-xs font-medium text-b4l-text-muted">Greutatea coletului</div>

      <table className="w-full text-xs">
        <tbody>
          {date.linii.map((l) => (
            <tr key={l.productId} className="border-b border-b4l-border last:border-0">
              <td className="py-1 pr-2">{l.denumire}</td>
              <td className="py-1 pr-2 text-right tabular-nums text-b4l-text-muted">
                ×{l.cantitate}
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">
                {l.greutateUnitara != null ? (
                  `${l.greutateUnitara} g/buc`
                ) : (
                  <input
                    type="number"
                    min={1}
                    value={lipsa[l.productId] ?? ''}
                    onChange={(e) => setLipsa((x) => ({ ...x, [l.productId]: e.target.value }))}
                    placeholder="g/buc"
                    className="w-20 rounded border border-b4l-red px-1 py-0.5 text-right text-xs"
                  />
                )}
              </td>
              <td className="py-1 text-right tabular-nums font-medium">
                {l.subtotal != null ? `${l.subtotal} g` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {date.lipsesc > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-b4l-red">
            {date.lipsesc === 1
              ? 'Un produs n-are greutatea în catalog'
              : `${date.lipsesc} produse n-au greutatea în catalog`}
            {date.calculat > 0 ? ` · restul adună ${date.calculat} g` : ''}
          </span>
          <Button
            className="!px-2 !py-1 !text-xs"
            disabled={deCompletat === 0}
            loading={completeaza.isPending}
            onClick={() => completeaza.mutate()}
          >
            Salvează în catalog
          </Button>
        </div>
      )}

      <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
        Greutatea trimisă la FAN (grame)
        <input
          type="number"
          min={1}
          value={greutate}
          onChange={(e) => setGreutate(e.target.value)}
          className="w-40 rounded border border-b4l-border px-2 py-1.5 text-sm"
        />
        <span className="text-xs text-b4l-text-dim">
          {date.total != null
            ? 'Adunată din greutatea produselor. Mărește-o dacă ambalajul adaugă mult.'
            : 'Completează greutățile lipsă de mai sus, sau scrie direct cât cântărește coletul.'}
        </span>
      </label>
    </div>
  )
}

/**
 * Unde a ajuns coletul.
 *
 * Regula de stare vine din `lib/stareExpeditie`, aceeași pe care o folosește ecranul de
 * expediții: scrisă a doua oară aici, ar fi divergat, iar două ecrane ar fi spus lucruri diferite
 * despre același colet.
 *
 * Fără rând în urmărire NU e o eroare: borderoul lui FAN se aduce o dată pe zi, deci un colet
 * expediat acum zece minute încă n-are rând. Se spune ca atare.
 */
function StareColet({ stare }: { stare: Cerere['stareAwb'] }) {
  if (!stare) {
    return (
      <span
        className="text-xs text-b4l-text-dim"
        title="Expedițiile se aduc o dată pe zi, din borderoul FAN."
      >
        încă nepreluat de curier
      </span>
    )
  }
  const st = stareExpeditie({
    lastEventName: stare.ultimaMiscare,
    deliveredAt: stare.livratLa,
    returnAwbNumber: stare.returAwb,
    closedAt: stare.inchisLa,
    inReturn: stare.seIntoarce,
    blockedSince: stare.blocatDin,
    intarziere: stare.intarziere,
  })
  const unde = [stare.ultimaMiscare, stare.ultimaMiscareUnde].filter(Boolean).join(' · ')
  return (
    <span className={`text-xs ${st.clasa}`} title={unde || undefined}>
      {st.text}
    </span>
  )
}
